import apis from '../../utils/apis';

/**
 * Marketplace API (M0 walking skeleton).
 *
 * Contract: pons-backend-platform `domains/marketplace/README.md`. Every
 * response is `{ success: true, data }`; these helpers unwrap `data`. Money is
 * a decimal STRING ("1500.00") end to end - never parse it to a float here.
 *
 * The whole feature is behind `isMarketplaceEnabled()` (frontend build flag)
 * AND `GET /marketplace/status` (`enabled`). With the backend flag off every
 * route except `/status` returns 404 `MARKETPLACE_DISABLED`.
 */

const BASE = '/marketplace';

export type LeadStatus = 'draft' | 'triaged' | 'sent' | 'proposed' | 'engaged' | 'declined' | 'closed';
export type IssueType = 'employment' | 'tenancy' | 'family' | 'contract' | 'corporate' | 'debt' | 'immigration' | 'other';
export type Urgency = 'low' | 'medium' | 'high';
export type FeeBasis = 'fixed' | 'capped' | 'hourly' | 'milestone';
export type ProposalStatus = 'proposed' | 'countered' | 'accepted' | 'declined' | 'withdrawn';

export const FEE_BASES: FeeBasis[] = ['fixed', 'capped', 'hourly', 'milestone'];

export interface MarketplaceStatus {
  enabled: boolean;
  has_lawyer_profile: boolean;
}

export interface LeadDocument {
  file_uuid: string;
  name: string;
  size?: number;
}

/**
 * No advice to the client: the backend serves the client (lead owner) only
 * issue_type, jurisdiction, disclaimer and `model_meta.ai_status`. The
 * assessment fields (urgency, scope, prep, needs_lawyer) reach lawyers only,
 * hence optional here.
 */
export interface Triage {
  issue_type: IssueType;
  jurisdiction: string;
  urgency?: Urgency;
  needs_lawyer?: boolean;
  scope_est?: { size: 'small' | 'medium' | 'large'; summary: string };
  /** Documents a lawyer should request from the client. */
  prep?: string[];
  disclaimer: string;
  /** `ai_status`: the AI engine is refining this triage (`pending`), has (`done`),
   *  or gave up (`failed` - the keyword triage stands). Absent = keyword only. */
  model_meta?: { provider: string; version: string; deterministic: boolean;
    ai_status?: 'pending' | 'done' | 'failed' };
}

export interface Lead {
  uuid: string;
  status: LeadStatus;
  /** null when the lead is redacted (lawyer view). */
  issue_text: string | null;
  jurisdiction_hint: string | null;
  documents: LeadDocument[];
  document_count: number;
  triage: Triage | null;
  /** Owner view, once engaged: the matter the engagement opened. */
  matter_uuid?: string | null;
  redacted: boolean;
  is_owner: boolean;
  created_at: string;
  updated_at: string;
}

export interface LawyerProfile {
  uuid: string;
  profile_type: 'independent' | 'firm';
  display_name: string;
  practice_areas: string[];
  jurisdictions: string[];
  bio: string | null;
  rate_card: { basis: string } | null;
  availability: 'available' | 'limited' | 'unavailable';
  /** Effective: an approval past `verified_until` reads as 'pending' (re-check due). */
  verification_status: 'unverified' | 'pending' | 'verified' | 'firm_backed' | 'rejected';
  verified_until?: string | null;
  rating_avg: string | null;
  rating_count: number;
  visible: boolean;
}

export type LicenceType = 'advokat' | 'advokatfullmektig' | 'abogado';
/** Where a lawyer is licensed; decides the verification steps. */
export type LicenceCountry = 'NO' | 'ES';
export type VerificationStepStatus = 'missing' | 'pending' | 'passed' | 'failed';

/**
 * A lawyer's own verification (GET /marketplace/verification/me). Only a
 * verified lawyer can send proposals or be suggested to clients.
 */
export interface LawyerVerification {
  status: 'unverified' | 'pending' | 'verified' | 'rejected';
  verified_until: string | null;
  country?: LicenceCountry;
  countries?: LicenceCountry[];
  /** Approved before, but the 90 days have run out. */
  recheck_due: boolean;
  /** BankID is configured on this environment. */
  bankid_available: boolean;
  /** 'stub' = local/dev test login, never real BankID (the UI says so). */
  bankid_mode?: 'stub' | 'live' | null;
  identity: { status: VerificationStepStatus; at?: string | null; name?: string | null; provider?: string | null };
  firm: {
    status: VerificationStepStatus;
    at?: string | null;
    org_number?: string | null;
    name?: string | null;
    /** not_found | deleted | bankrupt | being_wound_up */
    reason?: string | null;
    licence_type: LicenceType | null;
    /** ES: NIF/CIF/NIE; the firm name is self-declared; VIES is a soft signal. */
    tax_id?: string | null;
    vies?: 'valid' | 'invalid' | 'unavailable' | null;
    bar_association?: string | null;
    licence_number?: string | null;
  };
  /** reason: the staff reviewer's rejection code (see licenceRejectionText). */
  licence: { status: VerificationStepStatus; at?: string | null; reason: string | null };
}

export interface PracticeInput {
  org_number: string;
  licence_type: LicenceType;
  supervising_lawyer?: string;
}

/** Spain: no free company register, so the firm name is the lawyer's own. */
export interface PracticeInputES {
  tax_id: string;
  firm_name: string;
  /** Colegio de la Abogacía */
  bar_association: string;
  /** Número de colegiado */
  licence_number: string;
}

export interface Match {
  lawyer_profile_uuid: string;
  display_name: string;
  practice_areas: string[];
  jurisdictions: string[];
  verification_status: string;
  rating_avg: string | null;
  score: number;
  reasons: string[];
}

export interface Proposal {
  uuid: string;
  lead_uuid: string;
  lawyer_profile_uuid: string;
  lawyer_display_name: string;
  fee_basis: FeeBasis;
  amount: string;
  currency: string;
  plan_summary: string;
  terms: string | null;
  status: ProposalStatus;
  created_at: string;
  /** Negotiation steps, oldest first; both sides see the same list. */
  history?: ProposalHistoryEntry[];
}

export type DeclineReason = 'price' | 'plan' | 'chose_other' | 'no_longer_needed' | 'other';
export const DECLINE_REASONS: DeclineReason[] = ['price', 'plan', 'chose_other', 'no_longer_needed', 'other'];

export interface ProposalHistoryEntry {
  by: 'client' | 'lawyer';
  action: 'declined' | 'requested_changes' | 'revised' | 'withdrawn';
  note?: string | null;
  /** requested_changes: the price the client would accept; revised: the new price. */
  amount?: string | null;
  reason?: DeclineReason | null;
  at: string;
}

export interface EscrowTransaction {
  uuid: string;
  amount: string;
  platform_fee: string;
  fee_pct: string;
  currency: string;
  escrow_state: string;
}

export interface FeeTerms {
  fee_basis: FeeBasis;
  amount: string;
  currency: string;
}

export interface AcceptResult {
  engagement: {
    uuid: string;
    proposal_uuid: string;
    status: string;
    matter_uuid: string;
    fee_terms: FeeTerms;
  };
  /** null for hourly: nothing is held at accept, escrow happens per invoice. */
  transaction: EscrowTransaction | null;
  /** The client as a contact in the lawyer's org (null if it couldn't be created). */
  client_contact_uuid?: string | null;
  matter_uuid: string;
  /** Lawyer-facing follow-ups (also sent to the lawyer as a notification). Never show to the client. */
  warnings?: string[];
}

export interface LawyerProfileInput {
  practice_areas: string[];
  jurisdictions: string[];
  bio?: string;
  rate_basis?: string;
  availability?: string;
}

export interface CreateLeadInput {
  issue_text: string;
  jurisdiction_hint?: string;
  documents?: LeadDocument[];
}

export interface CreateProposalInput {
  fee_basis: FeeBasis;
  amount: string;
  currency?: string;
  plan_summary: string;
  terms?: string;
}

const unwrap = <T>(res: { data: { data: T } }): T => res.data.data;

export const getMarketplaceStatus = async (): Promise<MarketplaceStatus> =>
  unwrap<MarketplaceStatus>(await apis.get(`${BASE}/status`));

export const getMyLawyerProfile = async (): Promise<LawyerProfile | null> =>
  unwrap<LawyerProfile | null>(await apis.get(`${BASE}/lawyer-profile/me`));

export const upsertMyLawyerProfile = async (body: LawyerProfileInput): Promise<LawyerProfile> =>
  unwrap<LawyerProfile>(await apis.put(`${BASE}/lawyer-profile/me`, body));

export const getMyVerification = async (): Promise<LawyerVerification> =>
  unwrap<LawyerVerification>(await apis.get(`${BASE}/verification/me`));

/** Where to send the lawyer for BankID (a provider URL, or the dev stub's callback). */
export const startBankIdVerification = async (): Promise<{ authorize_url: string }> =>
  unwrap<{ authorize_url: string }>(await apis.post(`${BASE}/verification/identity/start`));

export const completeBankIdVerification = async (code: string, state: string): Promise<LawyerVerification> =>
  unwrap<LawyerVerification>(await apis.post(`${BASE}/verification/identity/complete`, { code, state }));

export const setVerificationCountry = async (country: LicenceCountry): Promise<LawyerVerification> =>
  unwrap<LawyerVerification>(await apis.post(`${BASE}/verification/country`, { country }));

export const submitPractice = async (body: PracticeInput | PracticeInputES): Promise<LawyerVerification> =>
  unwrap<LawyerVerification>(await apis.post(`${BASE}/verification/practice`, body));

/** The lawyer can't take this request; they won't see it again. */
export const declareConflict = async (leadUuid: string): Promise<{ lead_uuid: string; declared: boolean }> =>
  unwrap<{ lead_uuid: string; declared: boolean }>(
    await apis.post(`${BASE}/leads/${encodeURIComponent(leadUuid)}/conflict`));

/** Which side of an engagement a call acts as; the server checks it. */
export type EngagementRole = 'client' | 'lawyer';

/** The client portal's summary of an engagement (GET /leads/<uuid>/engagement). */
export interface EngagementView {
  engagement_uuid: string;
  status: 'active' | 'completed' | 'cancelled' | string;
  started_at: string | null;
  fee_terms: { fee_basis?: FeeBasis; amount?: string; currency?: string };
  plan_summary: string | null;
  lawyer: { display_name: string; firm_name: string | null };
  /** Messages from the other side not yet read. */
  unread: number;
  can_message: boolean;
  /** Lawyer side only. */
  client?: { display_name: string };
  matter_uuid?: string | null;
  /** The client sees it only once issued. */
  letter_status?: LetterStatus | null;
}

export interface EngagementMessage {
  uuid: string;
  from: EngagementRole;
  mine: boolean;
  body: string;
  created_at: string | null;
  read: boolean;
}

export const getEngagement = async (leadUuid: string, as: EngagementRole): Promise<EngagementView> =>
  unwrap<EngagementView>(await apis.get(`${BASE}/leads/${encodeURIComponent(leadUuid)}/engagement`, { params: { as } }));

export const listMessages = async (leadUuid: string, as: EngagementRole): Promise<EngagementMessage[]> =>
  unwrap<EngagementMessage[]>(await apis.get(`${BASE}/leads/${encodeURIComponent(leadUuid)}/messages`, { params: { as } }));

export const sendMessage = async (leadUuid: string, as: EngagementRole, body: string): Promise<EngagementMessage> =>
  unwrap<EngagementMessage>(await apis.post(`${BASE}/leads/${encodeURIComponent(leadUuid)}/messages`, { as, body }));

/** A file shared between a client and their hired lawyer. The file lives in the matter. */
export interface SharedDocument {
  uuid: string;
  /** to_lawyer: the client added it; to_client: the lawyer shared it. */
  direction: 'to_lawyer' | 'to_client';
  name: string;
  size: number | null;
  shared_at: string | null;
  mine: boolean;
}

/** The lawyer's picker: the matter's files. */
export interface MatterDocument {
  file_uuid: string;
  name: string;
  size: number | null;
  uploaded_at: string | null;
  shared: boolean;
  from_client: boolean;
}

const leadPath = (leadUuid: string) => `${BASE}/leads/${encodeURIComponent(leadUuid)}`;

export const listSharedDocuments = async (leadUuid: string, as: EngagementRole): Promise<SharedDocument[]> =>
  unwrap<SharedDocument[]>(await apis.get(`${leadPath(leadUuid)}/shared-documents`, { params: { as } }));

/** The client adds files after hiring; they go into the lawyer's matter. */
export const addSharedDocuments = async (leadUuid: string, files: File[]):
  Promise<{ added: number; skipped: { file_name: string; reason: string }[]; documents: SharedDocument[] }> => {
  const form = new FormData();
  form.append('as', 'client');
  files.forEach((f) => form.append('files', f));
  return unwrap(await apis.post(`${leadPath(leadUuid)}/shared-documents`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }));
};

export const listMatterDocuments = async (leadUuid: string): Promise<MatterDocument[]> =>
  unwrap<MatterDocument[]>(await apis.get(`${leadPath(leadUuid)}/matter-documents`, { params: { as: 'lawyer' } }));

export const shareDocument = async (leadUuid: string, fileUuid: string): Promise<SharedDocument> =>
  unwrap<SharedDocument>(await apis.post(`${leadPath(leadUuid)}/shared-documents/share`, { as: 'lawyer', file_uuid: fileUuid }));

export const unshareDocument = async (leadUuid: string, docUuid: string): Promise<{ uuid: string; revoked: boolean }> =>
  unwrap(await apis.post(`${leadPath(leadUuid)}/shared-documents/${encodeURIComponent(docUuid)}/unshare`, { as: 'lawyer' }));

/** Same pattern as the matter's downloadDocument: decrypted server-side, saved by the browser. */
export const downloadSharedDocument = async (leadUuid: string, as: EngagementRole, doc: SharedDocument): Promise<void> => {
  const response = await apis.get(`${leadPath(leadUuid)}/shared-documents/${encodeURIComponent(doc.uuid)}/download`,
    { params: { as }, responseType: 'blob' });
  const url = window.URL.createObjectURL(response.data as Blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = doc.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const createLead = async (body: CreateLeadInput): Promise<Lead> =>
  unwrap<Lead>(await apis.post(`${BASE}/leads`, body));

export const listLeads = async (scope: 'mine' | 'inbox'): Promise<Lead[]> =>
  unwrap<Lead[]>(await apis.get(`${BASE}/leads`, { params: { scope } }));

export const getLead = async (leadUuid: string): Promise<Lead> =>
  unwrap<Lead>(await apis.get(`${BASE}/leads/${encodeURIComponent(leadUuid)}`));

export const triageLead = async (leadUuid: string): Promise<Lead> =>
  unwrap<Lead>(await apis.post(`${BASE}/leads/${encodeURIComponent(leadUuid)}/triage`));

export const getMatches = async (leadUuid: string): Promise<Match[]> =>
  unwrap<Match[]>(await apis.get(`${BASE}/leads/${encodeURIComponent(leadUuid)}/matches`));

export const createProposal = async (leadUuid: string, body: CreateProposalInput): Promise<Proposal> =>
  unwrap<Proposal>(await apis.post(`${BASE}/leads/${encodeURIComponent(leadUuid)}/proposals`, body));

export const listProposals = async (leadUuid: string): Promise<Proposal[]> =>
  unwrap<Proposal[]>(await apis.get(`${BASE}/leads/${encodeURIComponent(leadUuid)}/proposals`));

export const acceptProposal = async (proposalUuid: string): Promise<AcceptResult> =>
  unwrap<AcceptResult>(await apis.post(`${BASE}/proposals/${encodeURIComponent(proposalUuid)}/accept`));

const proposalStep = (step: string) => async (proposalUuid: string, body: object = {}): Promise<Proposal> =>
  unwrap<Proposal>(await apis.post(`${BASE}/proposals/${encodeURIComponent(proposalUuid)}/${step}`, body));

/** Client: the proposal is out. */
export const declineProposal: (uuid: string, body: { reason?: DeclineReason; note?: string }) => Promise<Proposal> =
  proposalStep('decline');
/** Client: ask for changes (a note, optionally the price they'd accept). */
export const requestChanges: (uuid: string, body: { note: string; amount?: string }) => Promise<Proposal> =
  proposalStep('request-changes');
/** Lawyer: a new price / plan / terms. */
export const reviseProposal: (
  uuid: string, body: { amount?: string; plan_summary?: string; terms?: string; note?: string },
) => Promise<Proposal> = proposalStep('revise');
/** Lawyer: pull the proposal. */
export const withdrawProposal: (uuid: string, body: { note?: string }) => Promise<Proposal> = proposalStep('withdraw');

/** Engagement letter (oppdragsbekreftelse / hoja de encargo). */
export type LetterStatus = 'draft' | 'issued' | 'confirmed';

export interface EngagementLetter {
  /** 'pending': the client's view before the lawyer issued it. */
  status: LetterStatus | 'pending';
  text: string | null;
  /** Draft only: bracketed parts the lawyer must still fill in. */
  missing?: number;
  issued_at?: string | null;
  confirmed_at?: string | null;
  confirmations?: { party: EngagementRole; name: string; at: string; method: SignMethod }[];
  /** Issued letter: how the client signs — bankid, bankid_test (local test login) or typed_name (no provider). */
  sign_with?: SignMethod | null;
}

export type SignMethod = 'bankid' | 'bankid_test' | 'typed_name';

/** Session key: the request a BankID signing started from (for a cancelled return). */
export const SIGNING_LEAD_KEY = 'marketplace:signing-lead';
/** OIDC state prefix the backend gives a signing (the callback is shared with identity checks). */
export const SIGNING_STATE_PREFIX = 'sign.';

const letterUrl = (leadUuid: string, tail = '') => `${BASE}/leads/${encodeURIComponent(leadUuid)}/letter${tail}`;

/** The lawyer's first read drafts it from the agreed terms. */
export const getLetter = async (leadUuid: string, as: EngagementRole): Promise<EngagementLetter> =>
  unwrap<EngagementLetter>(await apis.get(letterUrl(leadUuid), { params: { as } }));

export const saveLetter = async (leadUuid: string, text: string): Promise<EngagementLetter> =>
  unwrap<EngagementLetter>(await apis.put(letterUrl(leadUuid), { as: 'lawyer', text }));

export const issueLetter = async (leadUuid: string): Promise<EngagementLetter> =>
  unwrap<EngagementLetter>(await apis.post(letterUrl(leadUuid, '/issue'), { as: 'lawyer' }));

export const confirmLetter = async (leadUuid: string, fullName: string): Promise<EngagementLetter> =>
  unwrap<EngagementLetter>(await apis.post(letterUrl(leadUuid, '/confirm'), { as: 'client', full_name: fullName }));

export const startLetterSigning = async (leadUuid: string): Promise<{ authorize_url: string }> =>
  unwrap<{ authorize_url: string }>(await apis.post(letterUrl(leadUuid, '/sign/start'), { as: 'client' }));

export const completeLetterSigning = async (code: string, state: string): Promise<EngagementLetter & { lead_uuid: string }> =>
  unwrap<EngagementLetter & { lead_uuid: string }>(await apis.post(`${BASE}/letter/sign/complete`, { code, state }));

/** HTTP status of a failed marketplace call, if any (409 = already proposed). */
export const errorStatus = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } })?.response?.status;

/** Backend `error` string from a failed call, falling back to `fallback`. */
export const errorMessage = (err: unknown, fallback: string): string => {
  const data = (err as { response?: { data?: { error?: unknown } } })?.response?.data;
  return typeof data?.error === 'string' && data.error ? data.error : fallback;
};

/** A matter's marketplace origin (the engagement that opened it). */
export interface MatterEngagement {
  engagement_uuid: string;
  status: string;
  lead_uuid: string | null;
  issue_type: IssueType | null;
  fee_terms: FeeTerms;
  transaction: EscrowTransaction | null;
  client_contact_uuid: string | null;
  engaged_at: string | null;
}

/** null = the matter did not come from the marketplace. */
export const getEngagementForMatter = async (matterUuid: string): Promise<MatterEngagement | null> =>
  unwrap<MatterEngagement | null>(await apis.get(`${BASE}/engagements/by-matter/${encodeURIComponent(matterUuid)}`));

export interface LeadDocumentsResult {
  lead: Lead;
  added: number;
  /** Files the server refused (type, content, size, or the 10-document cap). */
  skipped: { file_name: string; reason: string }[];
}

/**
 * Attach documents to the client's own request (multipart, field `files`).
 * Small, capped (10 per request) and re-validated server-side with the matter
 * upload pipeline - the same FormData-through-apis pattern as importApi.
 */
export const uploadLeadDocuments = async (leadUuid: string, files: File[]): Promise<LeadDocumentsResult> => {
  const form = new FormData();
  files.forEach((f) => form.append('files', f));
  return unwrap<LeadDocumentsResult>(
    await apis.post(`${BASE}/leads/${encodeURIComponent(leadUuid)}/documents`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  );
};

